import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Wallet } from '@asia/shared';
import { useNav } from '../../store/nav';
import { haptic } from '../../hooks/useTelegram';
import { useWallets } from '../../store/wallets';
import { useProfile } from '../../store/profile';
import { useMe } from '../../hooks/useMe';
import { patchMe } from '../../lib/me';
import { getIdentity } from '../../lib/identity';
import { openLink, LINKS } from '../../lib/links';
import { setLanguage } from '../../i18n';
import { FigIcon, type FigIconName } from '../FigIcon';
import { WalletAddressRow } from './WalletAddressRow';
import { WalletDetailSheet } from './WalletDetailSheet';
import { RenameWalletDialog } from './RenameWalletDialog';
import { DeleteWalletDialog } from './DeleteWalletDialog';
import { useTheme } from '../../store/theme';

/** Offered in the order a user scans them: explicit choices first, then the
 *  one that defers to Telegram. */
const THEMES = ['light', 'dark', 'system'] as const;

const LANGS = [
  { code: 'en', labelKey: 'profile.langEn' },
  { code: 'ru', labelKey: 'profile.langRu' },
] as const;

const TILE_GLOW = 'inset 0px 0px 5.6px 0px rgba(255,255,255,0.56)';

/** Colored icon tile — 32px rounded square with a white glyph and an inner
 *  white glow, matching the Figma profile rows (node 2:7065 etc.). */
function Tile({ icon, color }: { icon: FigIconName; color: string }) {
  return (
    <span
      className="grid size-8 shrink-0 place-items-center rounded-[10px] text-white"
      style={{ backgroundColor: color, boxShadow: TILE_GLOW }}
    >
      <FigIcon name={icon} size={20} />
    </span>
  );
}

/** A settings/support list row: colored tile + label + chevron.
 *  `danger` renders the label in red (destructive-ish rows, e.g. sessions). */
function Row({
  icon,
  tile,
  color,
  label,
  value,
  danger,
  onClick,
}: {
  icon?: FigIconName;
  /** Custom tile (e.g. a language-code badge) shown instead of an icon tile. */
  tile?: React.ReactNode;
  color?: string;
  label: string;
  /** Trailing status text before the chevron (e.g. saved email, On/Off). */
  value?: string;
  danger?: boolean;
  onClick?: () => void;
}) {
  return (
    <button
      onClick={() => {
        haptic();
        onClick?.();
      }}
      className="flex w-full items-center gap-[10px] active:opacity-70"
    >
      {tile ?? (icon && <Tile icon={icon} color={color ?? 'rgb(var(--accent))'} />)}
      <span className={`flex-1 text-left text-[14px] ${danger ? 'text-negative' : 'text-ink'}`}>
        {label}
      </span>
      {value && <span className="max-w-[150px] truncate text-[14px] text-muted">{value}</span>}
      <FigIcon name="arrowRight" size={16} className="text-subtle" />
    </button>
  );
}

/** Language-code badge tile (EN / RU) — used instead of an icon on the Language
 *  row, since the design has no globe glyph. */
function LangTile({ code }: { code: string }) {
  return (
    <span
      className="grid size-8 shrink-0 place-items-center rounded-[10px] text-[11px] font-bold uppercase text-white"
      style={{ backgroundColor: '#2ac42a', boxShadow: TILE_GLOW }}
    >
      {code}
    </span>
  );
}

/** Hairline divider between rows — inset from the left by the row's icon column
 *  (Figma lines start under the text), filling to the right edge at any width. */
function Divider({ inset }: { inset: number }) {
  return (
    <div className="flex w-full" style={{ paddingLeft: inset }}>
      <div className="h-px w-full bg-hairline" />
    </div>
  );
}

export function ProfileScreen() {
  const { t, i18n } = useTranslation();
  const push = useNav((s) => s.push);
  const setTab = useNav((s) => s.setTab);
  const wallets = useWallets((s) => s.wallets);
  const renameWallet = useWallets((s) => s.rename);
  const removeWallet = useWallets((s) => s.remove);
  // Server truth (real Telegram); falls back to the local store in dev/browser.
  const { me, token } = useMe();
  const localEmail = useProfile((s) => s.email);
  const localPin = useProfile((s) => s.pinSet);
  const localBio = useProfile((s) => s.biometricEnabled);
  // With a real user, the server is the only truth (no stale local placeholder).
  const email = me ? (me.email ?? '') : localEmail;
  const pinSet = me?.pinSet ?? localPin;
  const biometricEnabled = me?.biometricEnabled ?? localBio;

  // Same wallet-management flow as the wallet screen (WalletSwitcher).
  const [detail, setDetail] = useState<Wallet | null>(null);
  const [renaming, setRenaming] = useState<Wallet | null>(null);
  const [deleting, setDeleting] = useState<Wallet | null>(null);
  const [langOpen, setLangOpen] = useState(false);
  const [themeOpen, setThemeOpen] = useState(false);
  const themePref = useTheme((st) => st.preference);
  const setThemePreference = useTheme((st) => st.setPreference);

  const { name, handle, photo, initial } = getIdentity();
  const primary = wallets[0]; // may be briefly undefined during a wallet reset
  const lang = i18n.language?.startsWith('ru') ? 'ru' : 'en';
  const onOff = (on: boolean) => (on ? t('profile.on') : t('profile.off'));

  return (
    <div className="min-h-full bg-surface px-4 pb-40 pt-6">
      <div className="flex w-full flex-col items-center gap-8">
        {/* profile header — Telegram account name / @username / photo */}
        <div className="flex flex-col items-center gap-[10px]">
          {photo ? (
            <img
              src={photo}
              alt=""
              className="size-24 rounded-full object-cover"
            />
          ) : (
            <div className="grid size-24 place-items-center rounded-full bg-gradient-to-br from-[#5cb6f8] to-[#2152f1] text-[34px] font-semibold text-white">
              {initial}
            </div>
          )}
          <div className="flex flex-col items-center gap-[2px]">
            <p className="text-[24px] font-medium leading-none text-ink">{name}</p>
            {handle && <p className="text-[14px] leading-none text-muted">{handle}</p>}
          </div>
        </div>

        {/* sections */}
        <div className="flex w-full flex-col gap-4">
          {/* wallet addresses — the primary wallet, then either "manage all
              wallets" (many) or a quick "add address" (only one), so the profile
              stays short no matter how many wallets exist. */}
          <div className="flex w-full flex-col gap-4 rounded-[24px] bg-card p-4">
            {primary && (
              <>
                <WalletAddressRow wallet={primary} onClick={() => setDetail(primary)} />
                <Divider inset={46} />
              </>
            )}
            {wallets.length > 1 ? (
              <button
                onClick={() => {
                  haptic();
                  push({ name: 'walletList' });
                }}
                className="flex items-center justify-between active:opacity-70"
              >
                <span className="flex items-center gap-[6px]">
                  <FigIcon name="navWallet" size={20} className="text-accent" />
                  <span className="text-[14px] text-accent">{t('profile.manageWallets')}</span>
                </span>
                <span className="flex items-center gap-2 text-[14px] text-muted">
                  {wallets.length}
                  <FigIcon name="arrowRight" size={16} className="text-subtle" />
                </span>
              </button>
            ) : (
              <button
                onClick={() => {
                  haptic();
                  push({ name: 'wallets' });
                }}
                className="flex items-center gap-[6px] active:opacity-70"
              >
                <FigIcon name="add" size={20} className="text-accent" />
                <span className="text-[14px] text-accent">{t('profile.addAddress')}</span>
              </button>
            )}
          </div>

          {/* cards */}
          <div className="rounded-[24px] bg-card p-3">
            <Row icon="pfCard" color="rgb(var(--accent))" label={t('profile.cards')} onClick={() => setTab('cards')} />
          </div>

          {/* account settings */}
          <div className="flex w-full flex-col gap-2 rounded-[24px] bg-card p-3">
            <Row
              icon="pfEmail"
              color="#8458db"
              label={t('profile.email')}
              value={email || t('profile.set')}
              onClick={() => push({ name: 'email' })}
            />
            <Divider inset={42} />
            <Row
              icon="pfSeed"
              color="#ff8228"
              label={t('profile.seedPhrase')}
              onClick={() => push({ name: 'seed' })}
            />
            <Divider inset={42} />
            <Row
              icon="pfPin"
              color="rgb(var(--accent))"
              label={t('profile.pin')}
              value={onOff(pinSet)}
              onClick={() => push({ name: 'pin' })}
            />
            <Divider inset={42} />
            <Row
              icon="pfFaceId"
              color="#2152f1"
              label={t('profile.biometric')}
              value={onOff(biometricEnabled)}
              onClick={() => push({ name: 'biometric' })}
            />
          </div>

          {/* support & preferences */}
          <div className="flex w-full flex-col gap-2 rounded-[24px] bg-card p-3">
            {/* appearance — same popover pattern as the language row below */}
            <div className="relative">
              <Row
                icon="pfSun"
                color="rgb(var(--accent))"
                label={t('profile.theme')}
                value={t(`profile.theme${themePref[0]!.toUpperCase()}${themePref.slice(1)}`)}
                onClick={() => setThemeOpen((o) => !o)}
              />
              {themeOpen && (
                <>
                  <div className="fixed inset-0 z-30" onClick={() => setThemeOpen(false)} aria-hidden />
                  <div
                    className="fade-in absolute right-0 top-[calc(100%+6px)] z-40 flex w-[180px] flex-col overflow-hidden rounded-[16px] bg-card"
                    style={{ boxShadow: '0px 0px 30px rgba(0,0,0,0.14)' }}
                  >
                    {THEMES.map((opt) => (
                      <button
                        key={opt}
                        onClick={() => {
                          haptic();
                          setThemePreference(opt);
                          setThemeOpen(false);
                        }}
                        className="flex items-center justify-between px-4 py-3 text-left text-[14px] text-ink active:bg-press/[0.04]"
                      >
                        {t(`profile.theme${opt[0]!.toUpperCase()}${opt.slice(1)}`)}
                        {opt === themePref && <FigIcon name="pfCheck" size={16} className="text-accent" />}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
            <Divider inset={42} />
            {/* language selector — inline popover, persists the choice */}
            <div className="relative">
              <Row
                tile={<LangTile code={lang} />}
                label={t('profile.language')}
                value={t(lang === 'ru' ? 'profile.langRu' : 'profile.langEn')}
                onClick={() => setLangOpen((o) => !o)}
              />
              {langOpen && (
                <>
                  <div className="fixed inset-0 z-30" onClick={() => setLangOpen(false)} aria-hidden />
                  <div
                    className="fade-in absolute right-0 top-[calc(100%+6px)] z-40 flex w-[180px] flex-col overflow-hidden rounded-[16px] bg-card"
                    style={{ boxShadow: '0px 0px 30px rgba(0,0,0,0.14)' }}
                  >
                    {LANGS.map((l) => {
                      const on = l.code === lang;
                      return (
                        <button
                          key={l.code}
                          onClick={() => {
                            haptic();
                            setLanguage(l.code);
                            if (token) patchMe(token, { language: l.code }).catch(() => {});
                            setLangOpen(false);
                          }}
                          className="flex items-center justify-between px-4 py-3 text-left text-[14px] text-ink active:bg-press/[0.04]"
                        >
                          {t(l.labelKey)}
                          {on && <FigIcon name="pfCheck" size={16} className="text-accent" />}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
            <Divider inset={42} />
            <Row
              icon="pfReceipt"
              color="#f0a020"
              label={t('profile.invite')}
              onClick={() => push({ name: 'referral' })}
            />
            <Divider inset={42} />
            <Row
              icon="pfSupport"
              color="rgb(var(--accent))"
              label={t('profile.support')}
              onClick={() => push({ name: 'support' })}
            />
            <Divider inset={42} />
            <Row
              icon="pfPolicy"
              color="#2ac42a"
              label={t('profile.terms')}
              onClick={() => openLink(LINKS.terms)}
            />
            <Divider inset={42} />
            <Row
              icon="pfLock"
              color="#8458db"
              label={t('profile.privacy')}
              onClick={() => openLink(LINKS.privacy)}
            />
            <Divider inset={42} />
            <Row icon="pfFaq" color="#2152f1" label={t('profile.faq')} onClick={() => push({ name: 'faq' })} />
          </div>

          {/* session management — separate block, destructive red */}
          <div className="rounded-[24px] bg-card p-3">
            <Row
              icon="pfSetting"
              color="rgb(var(--negative))"
              label={t('profile.sessionMgmt')}
              danger
              onClick={() => push({ name: 'sessions' })}
            />
          </div>

          <p className="text-center text-[14px] text-muted">
            Asia Wallet v{__APP_VERSION__} · {__BUILD_ID__}
          </p>
        </div>
      </div>

      {detail && (
        <WalletDetailSheet
          wallet={detail}
          onClose={() => setDetail(null)}
          onSeed={() => {
            setDetail(null);
            push({ name: 'seed', walletId: detail.id });
          }}
          onRename={() => {
            setRenaming(detail);
            setDetail(null);
          }}
          onDelete={() => {
            setDeleting(detail);
            setDetail(null);
          }}
        />
      )}

      {renaming && (
        <RenameWalletDialog
          wallet={renaming}
          onConfirm={(newName) => {
            renameWallet(renaming.id, newName);
            setRenaming(null);
          }}
          onCancel={() => setRenaming(null)}
        />
      )}

      {deleting && (
        <DeleteWalletDialog
          wallet={deleting}
          onConfirm={() => {
            removeWallet(deleting.id);
            setDeleting(null);
          }}
          onCancel={() => setDeleting(null)}
        />
      )}
    </div>
  );
}
